(() => {
  if (window.mdImpactInitialized) return;
  window.mdImpactInitialized = true;
  const cleanups = new WeakMap();
  const motion = matchMedia('(prefers-reduced-motion: reduce)');
  function init(scope) {
    const roots = [...scope.querySelectorAll('[data-md-cinema], [data-md-univers], [data-md-look]')];
    if (scope.matches?.('[data-md-cinema], [data-md-univers], [data-md-look]')) roots.unshift(scope);
    roots.forEach(root => {
      if (cleanups.has(root)) return;
      const controller = new AbortController();
      const { signal } = controller;
      const listen = (target, event, handler, options = {}) => target?.addEventListener(event, handler, { ...options, signal });
      let timer;
      cleanups.set(root, () => { controller.abort(); clearInterval(timer); cleanups.delete(root); });
      if (root.matches('[data-md-cinema]')) {
        const slides = [...root.querySelectorAll('[data-md-slide]')];
        if (!slides.length) return;
        let current = Math.max(0, slides.findIndex(slide => !slide.hidden));
        const count = root.querySelector('[data-md-count]');
        root.dataset.mdCrossfade = '';
        let busy = false;
        let finishTimer;
        signal.addEventListener('abort', () => clearTimeout(finishTimer), { once: true });
        const ready = async slide => {
          const img = slide.querySelector('img');
          if (!img) return false;
          img.loading = 'eager';
          if (!img.complete) await new Promise((resolve, reject) => {
            const done = () => { clearTimeout(timeout); img.removeEventListener('load', loaded); img.removeEventListener('error', failed); signal.removeEventListener('abort', failed); };
            const loaded = () => { done(); resolve(); };
            const failed = () => { done(); reject(new Error('Image unavailable')); };
            const timeout = setTimeout(failed, 10000);
            img.addEventListener('load', loaded, { once: true });
            img.addEventListener('error', failed, { once: true });
            signal.addEventListener('abort', failed, { once: true });
          });
          if (!img.naturalWidth) return false;
          if (img.decode) await img.decode();
          return !signal.aborted;
        };
        const updateCount = () => {
          if (count) count.textContent = `${String(current + 1).padStart(2, '0')} / ${String(slides.length).padStart(2, '0')}`;
        };
        const preloadNext = () => {
          const slide = slides[(current + 1) % slides.length];
          const img = slide.querySelector('img');
          if (img) img.loading = 'eager';
        };
        slides.forEach((slide, i) => {
          slide.hidden = i !== current;
          slide.classList.toggle('is-current', i === current);
          slide.inert = i !== current;
          slide.setAttribute('aria-hidden', String(i !== current));
        });
        const show = async index => {
          const next = (index + slides.length) % slides.length;
          if (busy || next === current || signal.aborted) return;
          busy = true;
          const incoming = slides[next];
          const outgoing = slides[current];
          incoming.hidden = false;
          incoming.classList.add('is-incoming');
          try {
            if (!await ready(incoming)) throw new Error('Image unavailable');
          } catch {
            incoming.hidden = true;
            incoming.classList.remove('is-incoming');
            busy = false;
            return;
          }
          if (signal.aborted) return;
          outgoing.classList.add('is-outgoing');
          outgoing.classList.remove('is-current');
          outgoing.inert = true;
          outgoing.setAttribute('aria-hidden', 'true');
          // Flush the transparent incoming layer before raising its opacity.
          void incoming.offsetWidth;
          incoming.classList.add('is-current');
          incoming.inert = false;
          incoming.setAttribute('aria-hidden', 'false');
          current = next;
          updateCount();
          const finish = () => {
            outgoing.hidden = true;
            outgoing.classList.remove('is-outgoing');
            incoming.classList.remove('is-incoming');
            busy = false;
            preloadNext();
          };
          if (motion.matches) finish();
          else finishTimer = setTimeout(finish, 550);
        };
        updateCount();
        const firstImage = slides[current].querySelector('img');
        if (firstImage?.complete) preloadNext();
        else listen(firstImage, 'load', preloadNext, { once: true });
        listen(root.querySelector('[data-md-prev]'), 'click', () => show(current - 1));
        listen(root.querySelector('[data-md-next]'), 'click', () => show(current + 1));
        const controls = root.querySelector('.md-cinema-controls');
        let pause = controls?.querySelector('[data-md-pause]');
        if (!pause && controls && slides.length > 1) {
          pause = document.createElement('button');
          pause.type = 'button';
          pause.dataset.mdPause = '';
          controls.appendChild(pause);
        }
        let manualPaused = null;
        let hovered = false;
        const isPaused = () => manualPaused ?? motion.matches;
        const syncPause = () => {
          if (!pause) return;
          pause.textContent = isPaused() ? '▶' : 'Ⅱ';
          pause.setAttribute('aria-label', isPaused() ? 'Reprendre le défilement' : 'Mettre le défilement en pause');
          pause.setAttribute('aria-pressed', String(isPaused()));
        };
        listen(motion, 'change', syncPause);
        listen(pause, 'click', () => { manualPaused = !isPaused(); syncPause(); });
        listen(root, 'pointerenter', event => { if (event.pointerType === 'mouse') hovered = true; });
        listen(root, 'pointerleave', () => { hovered = false; });
        let start = null;
        listen(root, 'touchstart', event => {
          start = event.touches.length === 1 && !event.target.closest('a, button, input, select, textarea')
            ? { x: event.touches[0].clientX, y: event.touches[0].clientY, id: event.touches[0].identifier } : null;
        }, { passive: true });
        listen(root, 'touchcancel', () => { start = null; }, { passive: true });
        listen(root, 'touchend', event => {
          const touch = [...event.changedTouches].find(item => item.identifier === start?.id);
          if (touch && start) {
            const dx = touch.clientX - start.x;
            const dy = touch.clientY - start.y;
            if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy) * 1.5) show(current + (dx < 0 ? 1 : -1));
          }
          start = null;
        }, { passive: true });
        syncPause();
        updateCount();
        if (slides.length > 1) timer = setInterval(() => {
          const rect = root.getBoundingClientRect();
          if (root.isConnected && !isPaused() && !hovered && !document.hidden && !root.contains(document.activeElement) && rect.bottom > 0 && rect.top < innerHeight) show(current + 1);
        }, 5000);
      }
      if (root.matches('[data-md-univers]')) {
        const rail = root.querySelector('[data-md-rail]');
        if (!rail) return;
        const prev = root.querySelector('[data-md-rail-prev]');
        const next = root.querySelector('[data-md-rail-next]');
        const sync = () => {
          if (prev) prev.disabled = rail.scrollLeft <= 1;
          if (next) next.disabled = rail.scrollLeft + rail.clientWidth >= rail.scrollWidth - 1;
        };
        const move = direction => rail.scrollBy({ left: direction * rail.clientWidth * .8, behavior: motion.matches ? 'instant' : 'smooth' });
        listen(prev, 'click', () => move(-1));
        listen(next, 'click', () => move(1));
        listen(rail, 'scroll', sync, { passive: true });
        const observer = new ResizeObserver(sync);
        observer.observe(rail);
        signal.addEventListener('abort', () => observer.disconnect(), { once: true });
        sync();
      }
      if (root.matches('[data-md-look]')) {
        const points = [...root.querySelectorAll('[data-md-point]')];
        const cards = [...root.querySelectorAll('[data-md-look-product]')];
        if (!cards.length) return;
        let current = 0;
        const show = index => {
          current = (index + cards.length) % cards.length;
          points.forEach(point => point.setAttribute('aria-pressed', String(Number(point.dataset.mdPoint) === current)));
          cards.forEach((card, i) => { card.hidden = i !== current; });
          const count = root.querySelector('[data-md-look-count]');
          if (count) count.textContent = `${current + 1} / ${cards.length}`;
        };
        points.forEach(point => listen(point, 'click', () => {
          const index = Number(point.dataset.mdPoint);
          if (Number.isInteger(index) && index >= 0 && index < cards.length) show(index);
        }));
        listen(root.querySelector('[data-md-look-prev]'), 'click', () => show(current - 1));
        listen(root.querySelector('[data-md-look-next]'), 'click', () => show(current + 1));
        cards.forEach(card => listen(card.querySelector('[data-md-look-variant]'), 'change', event => {
          const price = card.querySelector('[data-md-price]');
          if (price && event.target.selectedOptions[0]) price.textContent = event.target.selectedOptions[0].dataset.price;
        }));
        show(0);
      }
    });
  }
  init(document);
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => {
    [event.target, ...event.target.querySelectorAll('[data-md-cinema], [data-md-univers], [data-md-look]')].forEach(root => cleanups.get(root)?.());
  });
})();
